import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import { cn } from '@workspace/ui/lib/utils';

import ChevronDownIcon from '~/components/MarketplaceIcons/ChevronDownIcon';

import { SECTION_TITLE_CLASS } from '../constants';

export interface SpecRow {
  key: string;
  value: string;
}

interface Props {
  rows: SpecRow[];
  description?: string;
  /** Extra block rendered before the specs, e.g. the items of a bundle. */
  extra?: { title: string; content: ReactNode } | null;
}

const FOLD_HEIGHT_CLASS = 'max-h-32';
const FOLD_ROWS = 4;
const FOLD_DESCRIPTION_LENGTH = 320;

const FoldButton = ({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onToggle}
      className="mt-2 flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 font-medium text-[#141415] text-[14px] leading-5 [font-family:inherit]"
    >
      <span>{open ? t('collapse') : t('showAll')}</span>
      <ChevronDownIcon
        className={cn(
          'size-4 shrink-0 transition-transform duration-200 ease-[cubic-bezier(.22,1,.36,1)]',
          open && 'rotate-180',
        )}
      />
    </button>
  );
};

const foldBoxClass = (canFold: boolean, open: boolean) =>
  canFold && !open
    ? `${FOLD_HEIGHT_CLASS} overflow-hidden [mask-image:linear-gradient(#000_40%,transparent)]`
    : '';

/**
 * Specs and description of the DubaiExpress product page side by side. Both
 * fold to the same 128px so the two fold buttons line up: specs over four
 * rows, a description over 320 characters.
 */
export const ProductDEDetails = ({ rows, description, extra }: Props) => {
  const { t } = useTranslation();
  const [specsOpen, setSpecsOpen] = useState(false);
  const [descriptionOpen, setDescriptionOpen] = useState(false);

  const canFoldSpecs = rows.length > FOLD_ROWS;
  const canFoldDescription =
    (description ?? '').length > FOLD_DESCRIPTION_LENGTH;

  if (!rows.length && !description && !extra) {
    return null;
  }

  return (
    <section className="mt-8 grid items-start gap-x-12 gap-y-6 min-[860px]:grid-cols-2">
      {extra && (
        <div>
          <h2 className={`${SECTION_TITLE_CLASS} mb-2!`}>{extra.title}</h2>
          {extra.content}
        </div>
      )}
      {rows.length > 0 && (
        <div>
          <h2 className={`${SECTION_TITLE_CLASS} mb-2!`}>
            {t('characteristics')}
          </h2>
          <div className={foldBoxClass(canFoldSpecs, specsOpen)}>
            <div className="grid grid-cols-1 gap-0">
              {rows.map((row, index) => (
                <div
                  key={`${index}:${row.key}`}
                  className="flex items-baseline gap-2 py-1.5"
                >
                  <span className="shrink-0 text-[#8A8A8A] text-[14px] leading-5">
                    {row.key}
                  </span>
                  <span className="flex-1 -translate-y-1 border-0 border-[#E1E1E1] border-b border-dotted" />
                  <span className="whitespace-pre-line text-right text-[#141415] text-[14px] leading-5">
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
          {canFoldSpecs && (
            <FoldButton
              open={specsOpen}
              onToggle={() => setSpecsOpen((open) => !open)}
            />
          )}
        </div>
      )}
      {description && (
        <div>
          <h2 className={`${SECTION_TITLE_CLASS} mb-2!`}>{t('description')}</h2>
          <div className={foldBoxClass(canFoldDescription, descriptionOpen)}>
            <div className="text-[#141415] text-[14px] leading-[22px] [&_img]:block [&_img]:h-auto [&_img]:max-w-full [&_li]:text-[14px] [&_li]:leading-[22px] [&_p+p]:mt-3! [&_p]:m-0 [&_p]:text-pretty [&_p]:text-[14px] [&_p]:leading-[22px] [&_ul]:m-0 [&_ul]:pl-[22px]">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
              >
                {description}
              </ReactMarkdown>
            </div>
          </div>
          {canFoldDescription && (
            <FoldButton
              open={descriptionOpen}
              onToggle={() => setDescriptionOpen((open) => !open)}
            />
          )}
        </div>
      )}
    </section>
  );
};
